import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Astro Visualise intro — a short cosmic-loading animation the user sees
 * after tapping the floating "Visualise" button on the iOS tab bar.
 *
 * Auto-navigates to /astro-visualization after ~1.8s. The animation is CSS
 * only (no dependencies) so it starts instantly on any device.
 */
export default function VisualiseIntroPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => navigate("/astro-visualization", { replace: true }), 1800);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <div
      data-testid="visualise-intro"
      className="fixed inset-0 z-[9999] overflow-hidden flex items-center justify-center"
      style={{
        background:
          "radial-gradient(circle at 50% 45%, #3B1E5C 0%, #1A0A2E 55%, #0B0518 100%)",
      }}
    >
      {/* Twinkling starfield — cheap CSS approach */}
      <div className="absolute inset-0" aria-hidden="true">
        {Array.from({ length: 40 }).map((_, i) => {
          const top = Math.random() * 100;
          const left = Math.random() * 100;
          const size = Math.random() * 2 + 1;
          const delay = Math.random() * 3;
          return (
            <span
              key={i}
              style={{
                position: "absolute",
                top: `${top}%`,
                left: `${left}%`,
                width: `${size}px`,
                height: `${size}px`,
                background: "#FFF8E7",
                borderRadius: "9999px",
                boxShadow: "0 0 6px #FFF8E7",
                animation: `twinkle 2.6s ease-in-out ${delay}s infinite`,
                opacity: 0.7,
              }}
            />
          );
        })}
      </div>

      {/* Center: sun with orbiting planets */}
      <div className="relative flex flex-col items-center gap-8">
        {/* Rotating orbit ring */}
        <div className="relative w-40 h-40">
          {/* Sun core */}
          <div
            className="absolute inset-0 m-auto w-16 h-16 rounded-full shadow-[0_0_40px_rgba(247,215,116,0.75)]"
            style={{
              background:
                "radial-gradient(circle, #FFF3D6 0%, #F7D774 30%, #E8A628 65%, #C97C0A 100%)",
              animation: "sunPulse 1.6s ease-in-out infinite",
              top: 0, left: 0, right: 0, bottom: 0,
            }}
          />
          {/* Two orbit rings, opposite spins */}
          <div
            className="absolute inset-0 rounded-full border border-amber-200/25"
            style={{ animation: "spin 3.5s linear infinite" }}
          >
            <span
              style={{
                position: "absolute", top: "-4px", left: "50%",
                transform: "translateX(-50%)",
                width: 10, height: 10, borderRadius: 9999,
                background: "#F7D774",
                boxShadow: "0 0 10px #F7D774",
              }}
            />
          </div>
          <div
            className="absolute inset-3 rounded-full border border-rose-200/20"
            style={{ animation: "spin 2.4s linear infinite reverse" }}
          >
            <span
              style={{
                position: "absolute", top: "-3px", left: "50%",
                transform: "translateX(-50%)",
                width: 8, height: 8, borderRadius: 9999,
                background: "#F472B6",
                boxShadow: "0 0 8px #F472B6",
              }}
            />
          </div>
        </div>

        {/* Copy */}
        <div className="text-center px-6">
          <div
            className="text-[11px] font-bold tracking-[0.28em] uppercase mb-2"
            style={{ color: "#F7D774" }}
          >
            Astro Visualise
          </div>
          <div
            className="font-serif-display text-2xl font-light leading-snug"
            style={{ color: "#FFF8E7" }}
          >
            Charting your life's cosmic road…
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-amber-200"
                style={{ animation: `dot 1.2s ease-in-out ${i * 0.18}s infinite` }}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.85); }
          50%      { opacity: 1;    transform: scale(1.1); }
        }
        @keyframes sunPulse {
          0%, 100% { transform: scale(1);    filter: brightness(1); }
          50%      { transform: scale(1.06); filter: brightness(1.2); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes dot {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50%      { opacity: 1;   transform: translateY(-3px); }
        }
      `}</style>
    </div>
  );
}
